import { drawLantern } from './lantern';
import { AMBER, INK, PAPER, circuitMark, disc, leaf, line, rounded, spark, text } from './paint';
import { PALETTES, drawCrate, drawDoor, drawKey, drawPlatform } from './world';

/** A composed, original illustration, not a simulated or purported online session. */
export function drawHero(ctx: CanvasRenderingContext2D, time: number, reducedMotion: boolean): void {
  const p = PALETTES.grove;
  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  // The winding brook lives below the playable-looking ridge and keeps the upper left quiet.
  ctx.beginPath();
  ctx.moveTo(758, 439);
  ctx.bezierCurveTo(643, 505, 808, 552, 735, 590);
  ctx.bezierCurveTo(677, 629, 785, 642, 726, 675);
  ctx.lineTo(991, 675);
  ctx.bezierCurveTo(875, 604, 953, 540, 758, 439);
  ctx.fillStyle = '#a8c9b9';
  ctx.fill();
  for (const [x, y, width] of [[731, 541, 60], [797, 573, 43], [740, 615, 61], [847, 651, 66]]) {
    line(ctx, [x, y, x + width, y], '#d9e5ce', 2);
    line(ctx, [x + 12, y + 7, x + width - 7, y + 7], '#81aea2', 1.2);
  }

  // Solid, gently rounded hills: moss on top, warm earth below.
  ctx.beginPath();
  ctx.moveTo(-12, 524);
  ctx.bezierCurveTo(147, 542, 237, 495, 321, 505);
  ctx.bezierCurveTo(413, 511, 450, 476, 519, 476);
  ctx.lineTo(623, 476);
  ctx.bezierCurveTo(651, 496, 622, 594, 657, 675);
  ctx.lineTo(-12, 675);
  ctx.closePath();
  ctx.fillStyle = '#9d8960';
  ctx.fill();
  ctx.strokeStyle = '#56684b';
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.save();
  ctx.clip();
  for (let row = 0; row < 6; row++) {
    ctx.beginPath();
    ctx.moveTo(-10, 550 + row * 27);
    ctx.bezierCurveTo(210, 566 + row * 26, 416, 501 + row * 25, 651, 505 + row * 28);
    ctx.strokeStyle = row % 2 ? '#ad9569' : '#8f7b58';
    ctx.lineWidth = 1;
    ctx.stroke();
  }
  ctx.restore();
  ctx.beginPath();
  ctx.moveTo(-5, 524);
  ctx.bezierCurveTo(147, 542, 237, 495, 321, 505);
  ctx.bezierCurveTo(413, 511, 450, 476, 519, 476);
  ctx.lineTo(623, 476);
  ctx.lineTo(625, 491);
  ctx.bezierCurveTo(497, 482, 450, 526, 321, 521);
  ctx.bezierCurveTo(234, 513, 136, 558, -5, 541);
  ctx.fillStyle = '#8eac70';
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(-5, 525);
  ctx.bezierCurveTo(147, 543, 237, 496, 321, 506);
  ctx.bezierCurveTo(413, 512, 450, 477, 519, 477);
  ctx.lineTo(622, 477);
  ctx.strokeStyle = '#d3d99b';
  ctx.lineWidth = 4;
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(829, 477);
  ctx.bezierCurveTo(907, 472, 959, 441, 1040, 449);
  ctx.bezierCurveTo(1129, 459, 1180, 431, 1212, 445);
  ctx.lineTo(1212, 675);
  ctx.lineTo(792, 675);
  ctx.bezierCurveTo(833, 600, 792, 515, 829, 477);
  ctx.closePath();
  ctx.fillStyle = '#99805a';
  ctx.fill();
  ctx.strokeStyle = '#556349';
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(829, 477);
  ctx.bezierCurveTo(907, 472, 959, 441, 1040, 449);
  ctx.bezierCurveTo(1129, 459, 1180, 431, 1212, 445);
  ctx.lineTo(1212, 462);
  ctx.bezierCurveTo(1100, 482, 1023, 456, 962, 473);
  ctx.bezierCurveTo(907, 488, 860, 489, 824, 494);
  ctx.fillStyle = '#829f6b';
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(830, 478);
  ctx.bezierCurveTo(907, 473, 959, 442, 1040, 450);
  ctx.bezierCurveTo(1129, 460, 1180, 432, 1212, 446);
  ctx.strokeStyle = '#c5d59b';
  ctx.lineWidth = 4;
  ctx.stroke();

  // A miniature waystation: split-oak shell, leaf shingles, warm arch and a brass bell.
  ctx.beginPath();
  ctx.moveTo(996, 448);
  ctx.lineTo(1003, 339);
  ctx.quadraticCurveTo(1000, 315, 1021, 304);
  ctx.lineTo(1107, 304);
  ctx.quadraticCurveTo(1125, 318, 1123, 339);
  ctx.lineTo(1133, 450);
  ctx.closePath();
  ctx.fillStyle = '#ad966c';
  ctx.fill();
  ctx.strokeStyle = '#4f624a';
  ctx.lineWidth = 2.5;
  ctx.stroke();
  for (const [x, y] of [[1014, 352], [1027, 320], [1120, 374], [1008, 408]]) {
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.bezierCurveTo(x - 4, y + 13, x + 4, y + 26, x, y + 39);
    ctx.strokeStyle = '#8a7856';
    ctx.lineWidth = 1.4;
    ctx.stroke();
  }
  for (let i = 0; i < 8; i++) leaf(ctx, 994 + i * 18, 325 - Math.sin(i / 7 * Math.PI) * 29, 38, -0.55 + i * 0.16, i % 2 ? '#81986b' : '#95a774');
  drawDoor(ctx, { x: 1037, y: 350, w: 68, h: 99 }, {
    unlocked: true, waiting: 0, total: 0, enteredColors: [], palette: p, time, reducedMotion, hero: true,
  });
  rounded(ctx, 1030, 322, 81, 20, 4);
  ctx.fillStyle = '#e8d9af';
  ctx.fill();
  ctx.strokeStyle = '#6d7051';
  ctx.lineWidth = 1.4;
  ctx.stroke();
  text(ctx, 'WAYSTATION', 1070.5, 332, 8.6, '#4a5d45', 'center', 800);
  line(ctx, [1125, 356, 1138, 351, 1138, 365], '#596b4e', 2);
  rounded(ctx, 1132, 361, 12, 12, 5);
  ctx.fillStyle = '#dcc071';
  ctx.fill();
  ctx.stroke();
  disc(ctx, 1138, 374, 2, '#6f6343');

  // Rope rails and individually joined planks visibly connect the two hills.
  for (const x of [621, 836]) {
    rounded(ctx, x - 4, 442, 8, 48, 3);
    ctx.fillStyle = '#8b7952';
    ctx.fill();
    ctx.strokeStyle = INK;
    ctx.lineWidth = 1.5;
    ctx.stroke();
    disc(ctx, x, 442, 5.5, '#c6aa70');
  }
  ctx.beginPath();
  ctx.moveTo(621, 452);
  ctx.quadraticCurveTo(730, 486, 836, 452);
  ctx.strokeStyle = '#6c7957';
  ctx.lineWidth = 3;
  ctx.stroke();
  for (let i = 0; i < 10; i++) {
    const xx = 626 + i * 20.2;
    const progress = i / 9;
    const yy = 477 + Math.sin(progress * Math.PI) * 15;
    drawPlatform(ctx, { id: `hero-plank-${i}`, x: xx, y: yy, w: 21, h: 12, kind: 'wood' }, p);
    if (i % 2 === 0) line(ctx, [xx + 4, yy, xx + 4, 454 + Math.sin(progress * Math.PI) * 15], '#798262', 1.4);
  }
  ctx.save();
  ctx.setLineDash([1, 9]);
  ctx.lineDashOffset = reducedMotion ? 0 : -time * 10;
  ctx.beginPath();
  ctx.moveTo(617, 506);
  ctx.quadraticCurveTo(730, 549, 872, 504);
  ctx.strokeStyle = '#f7cf76';
  ctx.lineWidth = 3.2;
  ctx.stroke();
  ctx.restore();
  circuitMark(ctx, 618, 506, 0, true, 8);
  circuitMark(ctx, 874, 503, 0, true, 8);

  drawCrate(ctx, { id: 'hero-parcel', x: 445, y: 444, w: 35, h: 35, vx: 0, vy: 0, grounded: true, weight: 2 });
  drawPlatform(ctx, { id: 'hero-plate', x: 852, y: 470, w: 72, h: 9, kind: 'wood' }, p);
  rounded(ctx, 855, 468, 66, 5, 2);
  ctx.fillStyle = '#e6be62';
  ctx.fill();
  ctx.strokeStyle = INK;
  ctx.lineWidth = 1.5;
  ctx.stroke();

  const pose = { time, reducedMotion, speed: 0, vy: 0, grounded: true, facing: 1 };
  drawLantern(ctx, { ...pose, x: 519, y: 408, scale: 1.7, color: 0, number: 1, phase: 0, carrying: true });
  drawLantern(ctx, { ...pose, x: 522, y: 340, scale: 1.7, color: 1, number: 2, phase: 1.5, celebration: 1 });
  drawLantern(ctx, { ...pose, x: 691, y: 391 + (reducedMotion ? 0 : Math.sin(time * 1.5) * 3), scale: 1.65, color: 2, number: 3, phase: 3, grounded: false, vy: -75 });
  drawLantern(ctx, { ...pose, x: 864, y: 401, scale: 1.7, color: 3, number: 4, phase: 4.6, facing: -1 });
  drawLantern(ctx, { ...pose, x: 974, y: 391, scale: 1.5, color: 4, number: 5, phase: 2, facing: -1, celebration: 1 });

  const keyY = 312 + (reducedMotion ? 0 : Math.sin(time * 2) * 2);
  ctx.beginPath();
  ctx.arc(619, keyY, 29, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(239,197,95,.12)';
  ctx.fill();
  drawKey(ctx, 619, keyY, 1.25, 0.3);
  spark(ctx, 651, keyY - 14, 6.5, AMBER);
  spark(ctx, 593, keyY - 22, 4.5, '#f9f2cf');
  spark(ctx, 636, keyY + 24, 3.5, '#d5a855');

  // A short garland frames the right third without covering the air above the stack.
  ctx.beginPath();
  ctx.moveTo(757, 227);
  ctx.quadraticCurveTo(951, 290, 1160, 223);
  ctx.strokeStyle = '#7e947c';
  ctx.lineWidth = 1.3;
  ctx.stroke();
  for (const [x, y] of [[801, 240], [882, 252], [964, 256], [1046, 250], [1123, 234]]) {
    line(ctx, [x, y, x, y + 10], '#81967b', 1.2);
    rounded(ctx, x - 4, y + 8, 8, 12, 3);
    ctx.fillStyle = '#e7cb83';
    ctx.fill();
    ctx.strokeStyle = '#6e815f';
    ctx.lineWidth = 1.2;
    ctx.stroke();
    line(ctx, [x, y + 11, x, y + 16], '#fff5cb', 1.8);
  }

  // Sparse pressed-flower details, not visual noise around the couriers.
  for (const [x, y, scale] of [[297, 503, 1], [384, 500, 0.7], [565, 477, 0.6], [941, 458, 0.7], [1165, 450, 1]]) {
    line(ctx, [x, y + 4, x + 1, y - 10 * scale], '#6e865b', 1.4);
    leaf(ctx, x, y - 1, 10 * scale, -0.8, '#a4bc80');
    for (let petal = 0; petal < 5; petal++) {
      disc(ctx, x + 1 + Math.cos(petal * Math.PI * 0.4) * 3.5 * scale, y - 10 * scale + Math.sin(petal * Math.PI * 0.4) * 3.5 * scale, 2.4 * scale, PAPER);
    }
    disc(ctx, x + 1, y - 10 * scale, 1.8 * scale, '#dcb762');
  }

  // A very low foreground bank anchors the composition and hides no useful foot contacts.
  ctx.beginPath();
  ctx.moveTo(0, 648);
  ctx.bezierCurveTo(128, 588, 227, 657, 371, 641);
  ctx.bezierCurveTo(515, 621, 537, 665, 642, 675);
  ctx.lineTo(0, 675);
  ctx.fillStyle = '#688970';
  ctx.fill();
  for (const [x, y] of [[47, 641], [172, 641], [286, 652], [1151, 624]]) {
    leaf(ctx, x, y, 28, -0.7, '#76987a');
    leaf(ctx, x, y, 40, 0.1, '#829f7c');
    leaf(ctx, x, y, 23, 0.8, '#a0b28a');
  }
  ctx.restore();
}
